/**
 * Suspicious v2 + ADR-0247 follow-up (founder 2026-10-18) — the
 * compact identity split under a grouped agent panel's header.
 *
 *   Verified   · {verified_visits} visits from {verified_addresses} addresses
 *   Unverified · {unverified_visits} visits from {unverified_addresses} addresses — not proven to be {Operator}
 *
 * Replaces the older "X of Y visits came from {Operator}'s verified
 * addresses" sentence so there is ONE statement, not two. Reads the
 * `summary` object the GET /v1/sites/:site_id/sender-addresses
 * endpoint returns (same role gate as the per-address list, but the
 * counts come back for every role — no IPs leak).
 *
 * Hidden until the summary resolves + the panel has at least one
 * attested beacon; a verified-only agent reads simply "Verified · N
 * visits from K addresses" (unverified line omitted when 0). The
 * unverified line goes amber when the agent carries probe traffic
 * (impostor-visible rows render under the same panel elsewhere).
 */

import { useEffect, useState } from 'react';
import { ShieldCheck, ShieldAlert } from 'lucide-react';

import type { SenderAddressIdentitySummary } from '@sill/types/api';

import { useOptionalHttp } from '../../api/http-context.js';
import { getSenderAddresses } from '../../api/threats.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface AgentIdentitySplitProps {
  readonly siteId: string;
  readonly agentKey: string;
  /** The operator label used in the "not proven to be {Operator}" tail. */
  readonly operator: string | null;
  /** Impostor / probing rows: colour the Unverified line amber. */
  readonly probing?: boolean;
}

type Load =
  | { readonly kind: 'loading' }
  | { readonly kind: 'empty' }
  | { readonly kind: 'ready'; readonly summary: SenderAddressIdentitySummary };

export function AgentIdentitySplit(props: AgentIdentitySplitProps): JSX.Element | null {
  const C = useC();
  const http = useOptionalHttp();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    if (http === null) { setLoad({ kind: 'empty' }); return; }
    setLoad({ kind: 'loading' });
    let live = true;
    void getSenderAddresses(http, props.siteId, props.agentKey)
      .then((r) => {
        if (!live) return;
        const summary = r.summary;
        if (summary.verified_visits + summary.unverified_visits === 0) setLoad({ kind: 'empty' });
        else setLoad({ kind: 'ready', summary });
      })
      .catch(() => { if (live) setLoad({ kind: 'empty' }); });
    return () => { live = false; };
  }, [http, props.siteId, props.agentKey]);

  if (load.kind !== 'ready') return null;
  const { verified_visits, verified_addresses, unverified_visits, unverified_addresses } = load.summary;
  const operator = props.operator !== null && props.operator.length > 0 ? props.operator : null;
  const unverifiedColour = props.probing === true ? C.amberFg : C.muted;

  return (
    <div data-testid="agent-identity-split" style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
      {verified_visits > 0 ? (
        <div
          data-testid="agent-identity-split-verified"
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: sansFont, fontSize: 13, color: C.body }}
        >
          <ShieldCheck size={13} color={C.limeFg} />
          <span>
            <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.limeFg, marginRight: 6 }}>VERIFIED</span>
            {verified_visits.toLocaleString('en-US')} visit{verified_visits === 1 ? '' : 's'} from {verified_addresses.toLocaleString('en-US')} address{verified_addresses === 1 ? '' : 'es'}
          </span>
        </div>
      ) : null}
      {unverified_visits > 0 ? (
        <div
          data-testid="agent-identity-split-unverified"
          style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: sansFont, fontSize: 13, color: C.body }}
        >
          <ShieldAlert size={13} color={unverifiedColour} />
          <span>
            <span style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: unverifiedColour, marginRight: 6 }}>UNVERIFIED</span>
            {unverified_visits.toLocaleString('en-US')} visit{unverified_visits === 1 ? '' : 's'} from {unverified_addresses.toLocaleString('en-US')} address{unverified_addresses === 1 ? '' : 'es'}
            {operator !== null ? (
              <>
                {' '}&mdash; not proven to be {operator}
              </>
            ) : null}
          </span>
        </div>
      ) : null}
    </div>
  );
}
